import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { ErrorState, Loading } from '@/ui/states'

/**
 * Sending address — the Connections panel's third card.
 *
 * WHY THIS CARD EXISTS, AND WHY IT IS NOT A CONNECT BUTTON
 * --------------------------------------------------------
 * The other two cards here wrap an OAuth grant: press Connect, leave AugRank,
 * come back connected. This one cannot. AugStash mails a confirmation link to
 * the address and it stays **pending** until somebody opens their inbox and
 * clicks it — which will not happen while they are looking at this screen. So
 * the card is a list with an add form, and *pending* is a normal resting state
 * rather than a failure.
 *
 * It also carries the three things the onboarding wizard's single step cannot: a
 * second address, which of them outreach actually uses, and removal. Until this
 * landed, the wizard's own message — *"check back in Settings"* — pointed at a
 * screen with no sending-address control on it.
 *
 * THE DISTINCTION THE BADGE DRAWS, AND WHY IT IS NOT `is_default`
 * ---------------------------------------------------------------
 * A row is marked **In use** from `in_use`, which the API computes with the same
 * function the send path uses. `is_default` alone would be wrong: an unverified
 * default sends nothing, so a card keyed on the flag would tell a tenant their
 * outreach was configured when it was not. `sending_as` at the top is the same
 * answer stated once, for the common case where somebody wants to know the
 * address and not the table.
 *
 * WHY `retrying` IS ITS OWN WORDING RATHER THAN `pending`'s
 * ---------------------------------------------------------
 * It rendered identically to `pending` at first, on the argument that the
 * tenant's next action is the same either way — wait, then press *Check again*.
 * That argument holds; the **copy** did not. `pending` says *we emailed you a
 * confirmation link*, and `retrying` is written by two different situations:
 * AugStash reporting `TEMPORARY_FAILURE` on an address it did try to mail, and
 * `SenderService.add_sender` failing to reach AugStash at all. In the second
 * there is no link, and telling somebody to go and look for one in their inbox
 * sends them to wait for mail that was never sent.
 *
 * So `retrying` says only what is true of both: this is not finished, try again
 * shortly. The state resolves itself on the next *Check again* or on the next
 * sweep, because `refresh` adopts the provider's sender by address if one exists
 * and registers it if one does not.
 */

type SenderList = Schema<'SenderListResponse'>
type Sender = Schema<'SenderIdentityOut'>

interface Shown {
  badge: string
  tone: string
  hint: string
}

const AWAITING: Shown = {
  badge: '⏳ Awaiting confirmation',
  tone: 'ar-badge--warning',
  hint: 'We emailed a confirmation link. Open it and click the link, then choose Check again.',
}

/**
 * `retrying`, which must not claim a link was sent — see the file docstring. It
 * is written both by a provider-side soft failure and by AugRank never reaching
 * the provider, and only one of those mailed anything.
 */
const UNFINISHED: Shown = {
  badge: '⏳ Still setting up',
  tone: 'ar-badge--warning',
  hint: 'We have not finished setting this address up. Choose Check again in a moment.',
}

/**
 * The words each state gets, and what the tenant should do about it.
 *
 * A `switch` rather than a keyed object: `state` arrives from the generated
 * schema as a plain `string`, so indexing a record with it is a lookup the type
 * checker cannot resolve — and what that would hide is the default, which is the
 * branch that matters. An unrecognised state means the API grew a word this
 * build does not know, and showing it as *could not be set up* is the safe
 * reading: visible, and recoverable by removing the address.
 */
function stateOf(sender: Sender): Shown {
  switch (sender.state) {
    case 'verified':
      return {
        badge: '✓ Verified',
        tone: 'ar-badge--success',
        hint: 'Outreach can be sent from this address.',
      }
    case 'pending':
      return AWAITING
    case 'retrying':
      return UNFINISHED
    default:
      return {
        badge: '✕ Could not be set up',
        tone: 'ar-badge--danger',
        hint: 'Check the address is right. Remove it and add it again, or use a different one.',
      }
  }
}

export function SenderCard(): JSX.Element {
  const senders = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/sender', { signal })),
    [],
  )

  const [address, setAddress] = useState('')
  const [displayName, setDisplayName] = useState('')

  const add = useMutation((body: { address: string; display_name: string | null }) =>
    call(() => api.POST('/api/v1/connections/sender', { body })),
  )
  const refresh = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/connections/sender/{sender_id}/refresh', {
        params: { path: { sender_id: id } },
      }),
    ),
  )
  const choose = useMutation((id: string) =>
    call(() =>
      api.PUT('/api/v1/connections/sender/{sender_id}/default', {
        params: { path: { sender_id: id } },
      }),
    ),
  )
  const remove = useMutation((id: string) =>
    call(() =>
      api.DELETE('/api/v1/connections/sender/{sender_id}', {
        params: { path: { sender_id: id } },
      }),
    ),
  )

  // Every mutation returns the whole list, so the card re-renders from the
  // response rather than re-fetching. That is not only a saved request: a
  // refetch would race the write on a slow connection and could paint the
  // previous state over the new one.
  const [written, setWritten] = useState<SenderList | null>(null)
  const data = written ?? senders.state.data
  const rows = data?.senders ?? []
  const busy = add.pending || refresh.pending || choose.pending || remove.pending

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const trimmed = address.trim()
    if (!trimmed) return
    const result = await add.run({
      address: trimmed,
      display_name: displayName.trim() || null,
    })
    if (result.ok) {
      setWritten(result.data)
      setAddress('')
      setDisplayName('')
    }
  }

  async function act(
    mutation: { run: (id: string) => Promise<{ ok: boolean; data?: SenderList }> },
    id: string,
  ): Promise<void> {
    const result = await mutation.run(id)
    if (result.ok && result.data) setWritten(result.data)
  }

  return (
    <section className="ar-card" id="sender" aria-labelledby="ar-sender-heading">
      <div className="ar-card__header">
        <h2 id="ar-sender-heading">Sending address</h2>
        <span className="ar-card__spacer" />
        {senders.state.status !== 'ready' ? null : data?.sending_as ? (
          <span className="ar-badge ar-badge--success">{`✓ Sending as ${data.sending_as}`}</span>
        ) : (
          <span className="ar-badge ar-badge--warning">⚠ No verified address</span>
        )}
      </div>

      <div className="ar-card__body">
        <p className="ar-muted">
          Outreach is sent from this address, and replies come back to it. We email a confirmation
          link to prove it is yours.
        </p>

        {senders.state.status === 'loading' ? (
          <Loading label="Loading sending addresses" />
        ) : senders.state.status === 'error' ? (
          <ErrorState failure={senders.state.failure} onRetry={senders.refetch} />
        ) : rows.length === 0 ? (
          /*
           * The empty state says what the address is *for* rather than "no
           * addresses yet". A tenant reaching this card has usually skipped the
           * wizard's step 4, and the question in their head is what they missed.
           */
          <p className="ar-empty">
            No sending address yet. Outreach cannot go out until one is added and confirmed.
          </p>
        ) : (
          <ul className="app-sender__list" aria-label="Sending addresses">
            {rows.map((sender) => {
              const shown = stateOf(sender)
              return (
                <li key={sender.id} className="app-sender__row">
                  <div className="app-sender__main">
                    <span className="app-sender__title">
                      {sender.display_name
                        ? `${sender.display_name} <${sender.address}>`
                        : sender.address}
                    </span>
                    <span className={`ar-badge ${shown.tone}`}>{shown.badge}</span>
                    {sender.in_use ? <span className="ar-badge ar-badge--info">In use</span> : null}
                    <span className="ar-muted">{shown.hint}</span>
                    {/* The provider's own word on an address that is not
                        verified. Two addresses on the live tenant sat "still
                        setting up" from 12 Sept with the refusal on the row and
                        nowhere on the screen; a week of *Check again* is not
                        something anybody can act on, and the code is. */}
                    {sender.state !== 'verified' && sender.provider_status ? (
                      <span className="ar-muted ar-mono">
                        The email service said: {sender.provider_status}
                      </span>
                    ) : null}
                  </div>
                  <div className="app-sender__actions">
                    {sender.state !== 'verified' ? (
                      <button
                        type="button"
                        className="ar-btn ar-btn--quiet"
                        disabled={busy}
                        onClick={() => void act(refresh, sender.id)}
                      >
                        Check again
                      </button>
                    ) : null}
                    {sender.state === 'verified' && !sender.in_use ? (
                      <button
                        type="button"
                        className="ar-btn ar-btn--quiet"
                        disabled={busy}
                        onClick={() => void act(choose, sender.id)}
                      >
                        Use this one
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ar-btn ar-btn--danger"
                      disabled={busy}
                      onClick={() => void act(remove, sender.id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {/* `Field` + `ar-input` + `Button`, like every other form in settings.
            This was a bare `<label class="ar-field"><input>` — S-B1-era markup
            from before the atlas had an input class — and rendered as the
            browser's 21px default control under a styled page (walkthrough
            2026-09-12). */}
        <form className="ar-stack" onSubmit={(event) => void submit(event)}>
          {/*
            The add form's failure renders under the field rather than as a
            toast: the two that matter — an address another organisation holds,
            and one that is not an address — are both about the field directly
            above, and a toast disappears while the tenant is still reading it.
          */}
          <Field label="Email address" required invalid={add.state.status === 'error'}>
            {(aria) => (
              <input
                className="ar-input"
                {...aria}
                type="email"
                value={address}
                onChange={(event) => {
                  setAddress(event.target.value)
                  add.reset()
                }}
                placeholder="priya@yourcompany.com"
                required
              />
            )}
          </Field>
          {add.state.status === 'error' ? (
            <p className="ar-field__error" role="alert">
              <span aria-hidden="true">✕</span>
              {add.state.failure.message}
            </p>
          ) : null}
          <Field label="From name" help="Optional. What the recipient sees before the address.">
            {(aria) => (
              <input
                className="ar-input"
                {...aria}
                type="text"
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value)
                }}
                placeholder="Priya at Your Company"
              />
            )}
          </Field>
          <div>
            <Button
              type="submit"
              tone="secondary"
              busy={add.pending}
              busyLabel="Adding"
              disabled={busy || !address.trim()}
            >
              Add address
            </Button>
          </div>
        </form>

        {/*
          Row-action failures are separate from the form's: a `Remove` that fails
          because the provider is down has nothing to do with what is typed above,
          and rendering it under the input would attach the wrong cause to it.
        */}
        {[refresh, choose, remove].map((mutation, index) =>
          mutation.state.status === 'error' ? (
            <p key={index} className="ar-field__error" role="alert">
              {mutation.state.failure.message}
            </p>
          ) : null,
        )}
      </div>
    </section>
  )
}
